import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  fetchCooperationSignals,
  fetchLifecycle,
  fetchProperty,
  type CooperationSignals,
  type LifecycleEvent,
  type PropertyDetail as PropertyDetailData,
  type PropertyLifecycle,
  type PropertyMember,
} from "../lib/api";
import { requireAuth } from "../lib/auth";

export function PropertyDetail() {
  const { id } = useParams<{ id: string }>();
  const [property, setProperty] = useState<PropertyDetailData | null>(null);
  const [lifecycle, setLifecycle] = useState<PropertyLifecycle | null>(null);
  const [cooperation, setCooperation] = useState<CooperationSignals | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lifecycleError, setLifecycleError] = useState(false);
  const [cooperationError, setCooperationError] = useState(false);
  const [coBrokerageRequested, setCoBrokerageRequested] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isCurrent = true;

    async function load() {
      if (!id) {
        setError("Property ID is missing.");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);
      setLifecycleError(false);
      setCooperationError(false);

      const jwt = requireAuth() ?? undefined;
      const [propertyResult, lifecycleResult, cooperationResult] =
        await Promise.allSettled([
          fetchProperty(id),
          fetchLifecycle(id),
          fetchCooperationSignals(id, jwt),
        ]);

      if (!isCurrent) return;

      if (propertyResult.status === "rejected") {
        setError(
          propertyResult.reason instanceof Error
            ? propertyResult.reason.message
            : "Unable to load this property.",
        );
      } else {
        setProperty(propertyResult.value);
      }

      if (lifecycleResult.status === "fulfilled") {
        setLifecycle(lifecycleResult.value);
      } else {
        setLifecycleError(true);
      }

      if (cooperationResult.status === "fulfilled") {
        setCooperation(cooperationResult.value);
      } else {
        setCooperationError(true);
      }

      setIsLoading(false);
    }

    void load();
    return () => {
      isCurrent = false;
    };
  }, [id]);

  if (isLoading) {
    return <DetailSkeleton />;
  }

  if (error || !property) {
    return (
      <section className="mx-auto max-w-4xl rounded-2xl border border-red-200 bg-white p-8 text-center shadow-sm">
        <h1 className="text-xl font-semibold text-slate-950">Property unavailable</h1>
        <p className="mt-2 text-sm text-red-700">{error ?? "Property not found."}</p>
        <Link className="mt-5 inline-flex font-semibold text-mls-600 hover:underline" to="/properties">
          Back to properties
        </Link>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-7xl">
      <Link className="text-sm font-semibold text-mls-600 hover:underline" to="/properties">
        ← Back to properties
      </Link>

      <header className="mt-5 flex flex-col gap-4 border-b border-slate-200 pb-7 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-mls-600">
            Canonical property
          </p>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-950">
            {property.neighborhood ?? "Unknown neighborhood"}
          </h1>
          <p className="mt-2 text-slate-600">
            {property.city ?? "Unknown city"} · ID {property.id}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge status={property.status} />
          <span className="rounded-full bg-emerald-50 px-3 py-1.5 text-sm font-semibold text-emerald-700">
            Freshness {property.freshness_score}/100
          </span>
        </div>
      </header>

      <div className="mt-7 grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2" title="Canonical fields">
          <dl className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            <Fact label="Price" value={formatPrice(property.price)} />
            <Fact label="Rooms" value={formatNumber(property.rooms)} />
            <Fact label="Area" value={property.sqm ? property.sqm.toLocaleString() + " m²" : "—"} />
            <Fact label="Floor" value={formatNumber(property.floor)} />
            <Fact label="First seen" value={formatDate(property.first_seen_at)} />
            <Fact label="Last updated" value={formatDate(property.last_updated_at)} />
          </dl>
          <div className="mt-6 border-t border-slate-100 pt-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Description
            </p>
            <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-700">
              {property.description ?? "No description available."}
            </p>
          </div>
        </Card>

        <Card title="Cooperation signals">
          {cooperation ? (
            <dl className="grid grid-cols-3 gap-3 lg:grid-cols-1">
              <Signal label="Brokers" value={cooperation.brokerCount} />
              <Signal label="Inquiries" value={cooperation.inquiryCount} />
              <Signal label="Updates" value={cooperation.updateCount} />
            </dl>
          ) : (
            <Unavailable
              message={
                cooperationError
                  ? "Broker authentication is required to view cooperation signals."
                  : "No cooperation signals are available."
              }
            />
          )}
          {cooperation ? (
            <p className="mt-4 text-xs text-slate-500">
              Signal updated {formatDate(cooperation.listingFreshness)}
            </p>
          ) : null}
          <button
            className="mt-5 w-full rounded-lg bg-mls-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-mls-600 disabled:cursor-not-allowed disabled:opacity-60"
            type="button"
            disabled={coBrokerageRequested}
            onClick={() => setCoBrokerageRequested(true)}
          >
            {coBrokerageRequested ? "Request recorded locally" : "Request Co-Brokerage"}
          </button>
        </Card>
      </div>

      <div className="mt-6 grid gap-6 xl:grid-cols-2">
        <Card title={"Cluster members (" + property.clusterMembers.length + ")"}>
          {property.clusterMembers.length ? (
            <div className="space-y-3">
              {property.clusterMembers.map((member) => (
                <ClusterMemberCard key={member.id} member={member} />
              ))}
            </div>
          ) : (
            <Unavailable message="No member records are attached to this cluster." />
          )}
        </Card>

        <Card title="Lifecycle Events">
          {lifecycle ? (
            <>
              <dl className="mb-6 grid grid-cols-2 gap-4">
                <Fact label="First seen" value={formatDate(lifecycle.firstSeenDate)} />
                <Fact label="Last updated" value={formatDate(lifecycle.lastUpdatedDate)} />
              </dl>
              <EventList events={sortLifecycleEvents(lifecycle)} />
            </>
          ) : (
            <Unavailable
              message={
                lifecycleError
                  ? "Lifecycle data could not be loaded."
                  : "No lifecycle data is available."
              }
            />
          )}
        </Card>
      </div>
    </section>
  );
}

function Card({
  title,
  children,
  className = "",
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <article
      className={
        "rounded-2xl border border-slate-200 bg-white p-6 shadow-sm " + className
      }
    >
      <h2 className="mb-5 text-lg font-semibold text-slate-950">{title}</h2>
      {children}
    </article>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </dt>
      <dd className="mt-1 text-sm font-medium text-slate-950">{value}</dd>
    </div>
  );
}

function Signal({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl bg-mls-50 p-4">
      <dt className="text-xs font-medium text-slate-500">{label}</dt>
      <dd className="mt-1 text-2xl font-semibold text-mls-900">{value}</dd>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const active = status === "active";
  return (
    <span
      className={
        "rounded-full px-3 py-1.5 text-sm font-semibold " +
        (active
          ? "bg-blue-50 text-blue-700"
          : "bg-slate-100 text-slate-600")
      }
    >
      {status}
    </span>
  );
}

function ClusterMemberCard({ member }: { member: PropertyMember }) {
  return (
    <div className="rounded-xl border border-slate-200 p-4">
      <div className="flex items-center justify-between gap-3">
        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold capitalize text-slate-700">
          {member.source_type}
        </span>
        <span className="text-xs text-slate-500">
          Updated {formatDate(member.updated_at)}
        </span>
      </div>
      <dl className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
        <Fact label="Price" value={formatPrice(member.price)} />
        <Fact label="Rooms" value={formatNumber(member.rooms)} />
        <Fact
          label="Area"
          value={member.sqm ? member.sqm.toLocaleString() + " m²" : "—"}
        />
        <Fact label="Status" value={member.status} />
      </dl>
      {member.source_url ? (
        <a
          className="mt-4 inline-flex text-xs font-semibold text-mls-600 hover:underline"
          href={member.source_url}
          target="_blank"
          rel="noreferrer"
        >
          View public source ↗
        </a>
      ) : null}
    </div>
  );
}

function EventList({ events }: { events: LifecycleEvent[] }) {
  return (
    <section className="border-t border-slate-100 py-5 first:border-t-0 first:pt-0">
      {events.length ? (
        <ol className="space-y-4">
          {events.map((event, index) => (
            <li
              className="grid grid-cols-[0.5rem_1fr] gap-3 text-sm"
              key={event.occurred_at + "-" + index}
            >
              <span className="mt-1.5 size-2 rounded-full bg-mls-600" />
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-mls-600">
                  {event.event_type.replaceAll("_", " ")}
                </p>
                <p className="mt-1 text-slate-700">
                  {event.old_value ?? "—"} → {event.new_value ?? "—"}
                </p>
                <time className="text-xs text-slate-500">
                  {formatDate(event.occurred_at)}
                </time>
              </div>
            </li>
          ))}
        </ol>
      ) : (
        <p className="text-sm text-slate-500">No lifecycle events recorded.</p>
      )}
    </section>
  );
}

function sortLifecycleEvents(lifecycle: PropertyLifecycle): LifecycleEvent[] {
  return [...lifecycle.priceChanges, ...lifecycle.statusChanges].sort(
    (left, right) =>
      new Date(right.occurred_at).getTime() -
      new Date(left.occurred_at).getTime(),
  );
}function Unavailable({ message }: { message: string }) {
  return (
    <p className="rounded-xl border border-dashed border-slate-300 p-5 text-sm leading-6 text-slate-500">
      {message}
    </p>
  );
}

function DetailSkeleton() {
  return (
    <section className="mx-auto max-w-7xl animate-pulse" aria-label="Loading property">
      <div className="h-4 w-32 rounded bg-slate-200" />
      <div className="mt-6 h-10 w-80 max-w-full rounded bg-slate-200" />
      <div className="mt-8 grid gap-6 lg:grid-cols-3">
        <div className="h-72 rounded-2xl bg-white lg:col-span-2" />
        <div className="h-72 rounded-2xl bg-white" />
      </div>
    </section>
  );
}

function formatPrice(value: number | null): string {
  return value === null
    ? "—"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      }).format(value);
}

function formatNumber(value: number | null): string {
  return value === null ? "—" : value.toLocaleString();
}

function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : new Intl.DateTimeFormat("en-US", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(date);
}